import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSwitchComponent,
  HlmAlertComponent,
  HLM_FORM_FIELD,
} from '../../../ui';

export interface ContactFormDialogData {
  phoneServiceId: number;
}

export interface ContactFormResult {
  isPerson: boolean;
  salutation: string | null;
  title: string | null;
  firstName: string | null;
  lastName: string | null;
  name: string | null;
  email: string | null;
  description: string | null;
  department: string | null;
  position: string | null;
  notifyDefaultDestination: boolean;
}

@Component({
  selector: 'cmt-contact-form-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL,
    ...HLM_FORM_FIELD,
    HlmBtnDirective, HlmIconComponent, HlmInputDirective,
    HlmSwitchComponent, HlmAlertComponent,
  ],
  template: `
    <hlm-dialog-surface size="md">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucideUserPlus" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Add Contact</h2>
          <p hlmDialogDescription>Create a new contact (person or organization) for this phone service.</p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        <form [formGroup]="form" (ngSubmit)="save()" class="flex flex-col gap-4">
          @if (errorMessage()) {
            <hlm-alert tone="error">
              <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
              <span>{{ errorMessage() }}</span>
            </hlm-alert>
          }

          <div class="py-1">
            <hlm-switch
              [checked]="form.controls.isPerson.value"
              (checkedChange)="form.controls.isPerson.setValue($any($event))"
              [label]="form.controls.isPerson.value ? 'Person' : 'Organization'"
            />
          </div>

          @if (form.controls.isPerson.value) {
            <div class="grid grid-cols-2 gap-3">
              <hlm-form-field>
                <label hlmLabel>Salutation</label>
                <input hlmInput formControlName="salutation" />
              </hlm-form-field>
              <hlm-form-field>
                <label hlmLabel>Title</label>
                <input hlmInput formControlName="title" />
              </hlm-form-field>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <hlm-form-field>
                <label hlmLabel>First Name</label>
                <input hlmInput formControlName="firstName" />
              </hlm-form-field>
              <hlm-form-field>
                <label hlmLabel>Last Name</label>
                <input hlmInput formControlName="lastName" />
              </hlm-form-field>
            </div>
          } @else {
            <hlm-form-field>
              <label hlmLabel>Organization Name</label>
              <input hlmInput formControlName="name" />
            </hlm-form-field>
          }

          <hlm-form-field>
            <label hlmLabel>Email</label>
            <input hlmInput formControlName="email" type="email" />
          </hlm-form-field>

          <hlm-form-field>
            <label hlmLabel>Description</label>
            <input hlmInput formControlName="description" />
          </hlm-form-field>

          <div class="grid grid-cols-2 gap-3">
            <hlm-form-field>
              <label hlmLabel>Department</label>
              <input hlmInput formControlName="department" />
            </hlm-form-field>
            <hlm-form-field>
              <label hlmLabel>Position</label>
              <input hlmInput formControlName="position" />
            </hlm-form-field>
          </div>

          <div class="py-1">
            <hlm-switch
              [checked]="form.controls.notifyDefaultDestination.value"
              (checkedChange)="form.controls.notifyDefaultDestination.setValue($any($event))"
              label="Notify default destinations"
            />
          </div>
        </form>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()">Cancel</button>
        <button hlmBtn (click)="save()" [disabled]="form.invalid">
          <hlm-icon name="lucideCheck" size="0.9rem" />
          Create Contact
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class ContactFormDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ContactFormDialogComponent, ContactFormResult>);
  readonly data: ContactFormDialogData = inject(MAT_DIALOG_DATA);

  // Holds last server-side error so the user sees what went wrong (signal-style via getter for simplicity)
  private _errorMessage: string | null = null;
  errorMessage = (): string | null => this._errorMessage;
  setError(msg: string | null): void { this._errorMessage = msg; }

  readonly form = new FormGroup({
    isPerson: new FormControl(true, { nonNullable: true }),
    salutation: new FormControl('', { nonNullable: true }),
    title: new FormControl('', { nonNullable: true }),
    firstName: new FormControl('', { nonNullable: true }),
    lastName: new FormControl('', { nonNullable: true }),
    name: new FormControl('', { nonNullable: true }),
    email: new FormControl('', { nonNullable: true }),
    description: new FormControl('', { nonNullable: true }),
    department: new FormControl('', { nonNullable: true }),
    position: new FormControl('', { nonNullable: true }),
    notifyDefaultDestination: new FormControl(false, { nonNullable: true }),
  });

  save(): void {
    if (this.form.invalid) return;
    const v = this.form.getRawValue();
    const result: ContactFormResult = {
      isPerson: v.isPerson,
      salutation: v.isPerson ? (v.salutation || null) : null,
      title: v.isPerson ? (v.title || null) : null,
      firstName: v.isPerson ? (v.firstName || null) : null,
      lastName: v.isPerson ? (v.lastName || null) : null,
      name: v.isPerson ? null : (v.name || null),
      email: v.email || null,
      description: v.description || null,
      department: v.department || null,
      position: v.position || null,
      notifyDefaultDestination: v.notifyDefaultDestination,
    };
    this.dialogRef.close(result);
  }
}
